import { z } from "zod";

export const ApprovalParcelViewSchema = z
  .object({
    id: z.string(),
    agentId: z.string(),
    conversation: z
      .object({ sessionId: z.string(), sessionKey: z.string(), platform: z.string() })
      .strict()
      .nullable(),
    title: z.string(),
    description: z.string(),
    reviewers: z.array(
      z
        .object({
          userId: z.string(),
          displayName: z.string().nullable(),
          specialistRole: z.enum(["owner", "editor"]),
          nominated: z.boolean(),
        })
        .strict(),
    ),
    submittedByUserId: z.string().nullable(),
    submittedByName: z.string().nullable(),
    state: z.enum([
      "capturing",
      "pending",
      "approved",
      "rejected",
      "expired",
      "cancelled",
      "running",
      "executed",
      "failed",
    ]),
    args: z.record(z.string(), z.unknown()).nullable(),
    createdAt: z.string(),
    expiresAt: z.string(),
    decision: z.enum(["approved", "rejected"]).nullable(),
    decidedByUserId: z.string().nullable(),
    decidedByName: z.string().nullable(),
    decidedAt: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    payloadAvailable: z.boolean(),
    files: z.array(
      z
        .object({
          sealedPath: z.string(),
          kind: z.enum(["script", "input", "skill", "manifest"]),
          sourceRef: z.string().nullable(),
          bytes: z.number().int().nonnegative(),
          sha256: z.string(),
        })
        .strict(),
    ),
    execution: z
      .object({
        state: z.enum(["unclaimed", "claimed", "running", "settled"]),
        outcome: z.enum(["succeeded", "failed", "partial", "uncertain", "stale"]).nullable(),
        claimedAt: z.string().nullable(),
        startedAt: z.string().nullable(),
        settledAt: z.string().nullable(),
        error: z.string().nullable(),
        publicationState: z.enum(["not_required", "pending", "published", "failed"]),
        publishedPath: z.string().nullable(),
        publicationError: z.string().nullable(),
        updatedAt: z.string(),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type ApprovalParcelView = z.infer<typeof ApprovalParcelViewSchema>;
export const ApprovalParcelListSchema = z
  .object({
    parcels: z.array(ApprovalParcelViewSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export const ApprovalParcelDecisionSchema = z
  .object({
    kind: z.enum(["decided", "already_decided", "expired", "cancelled"]),
    parcel: ApprovalParcelViewSchema,
  })
  .strict();
export const ApprovalPreferencesSchema = z
  .object({
    connections: z.array(
      z
        .object({
          connectionId: z.string(),
          platform: z.string(),
          label: z.string(),
          ready: z.boolean(),
          readinessDetail: z.string().optional(),
          supportsActions: z.boolean(),
          enabled: z.boolean(),
        })
        .strict(),
    ),
  })
  .strict();
export const ApprovalFileTextSchema = z
  .object({
    text: z.string(),
    truncated: z.boolean(),
  })
  .strict();
